/**
 * MoneySchool — Interactive Financial Education App
 * Copyright (c) 2026. All Rights Reserved.
 *
 * Root component. Holds app-wide state and switches between Kids mode
 * (Money Adventure) and Pro mode. Tracker data is lifted here so plan
 * generation in ProPlanScreen can seed the tracker via persistTracker.
 *
 * See repo README for licensing + IP notes.
 */

import { useState, useEffect, useRef } from "react";
import { track, setAnon } from "./lib/track";
import { WORLDS } from "./data/worlds";
import { PRO_WORLDS } from "./data/proWorlds";
import { EMPTY_CATS, loadTD, saveTD, loadSavings, saveSavings, loadDaily, saveDaily, loadParent, saveParent, loadRewards, saveRewards, loadMissions, saveMissions, loadBudget, saveBudget, loadChores, saveChores, PLAN_PRIORITY_KEY, loadLessonHistory, saveLessonHistory, loadKidsProgress, saveKidsProgress, loadProProgress, saveProProgress, clearPlanResult, clearPlanForm, type LessonEvent } from "./lib/storage";
import { STREAK_MILESTONES, STREAK_BADGES, getHighestBadge } from "./data/streakBadges";
import { EMPTY_REWARDS, REWARD_ITEMS, THEME_GRADIENTS } from "./data/rewardItems";
import { EMPTY_MISSIONS } from "./data/missions";
import { EMPTY_CHORES } from "./data/chores";
import { EMPTY_BUDGET } from "./data/budgetCats";

import Confetti from "./components/Confetti";
import BuildInfoBadge from "./components/BuildInfoBadge";
import InstallBanner from "./components/InstallBanner";
import FeedbackModal from "./components/FeedbackModal";
import HamburgerMenu from "./components/HamburgerMenu";
import LandingScreen from "./screens/LandingScreen";
import OnboardingQuestionnaire from "./screens/OnboardingQuestionnaire";
import SignInScreen from "./screens/SignInScreen";
import AgeGateScreen, { loadAgeGate, clearAgeGate } from "./screens/AgeGateScreen";
import ParentConsentScreen from "./screens/ParentConsentScreen";
import { useAuth } from "./lib/useAuth";
import WorldMap from "./screens/kids/WorldMap";
import WorldScreen from "./screens/kids/WorldScreen";
import LessonScreen from "./screens/kids/LessonScreen";
import ChatScreen from "./screens/kids/ChatScreen";
import ProgressScreen from "./screens/kids/ProgressScreen";
import GamesScreen from "./screens/kids/GamesScreen";
import KidsSavingsScreen from "./screens/kids/KidsSavingsScreen";
import ParentPortalScreen from "./screens/kids/ParentPortalScreen";
import LeaderboardScreen from "./screens/kids/LeaderboardScreen";
import WhatIfScreen from "./screens/kids/WhatIfScreen";
import ScenariosScreen from "./screens/kids/ScenariosScreen";

import ProDashboard from "./screens/pro/ProDashboard";
import ProWorldScreen from "./screens/pro/ProWorldScreen";
import ProLessonScreen from "./screens/pro/ProLessonScreen";
import ProProgressScreen from "./screens/pro/ProProgressScreen";
import ProPlanScreen from "./screens/pro/ProPlanScreen";
import ProTrackerScreen from "./screens/pro/ProTrackerScreen";
import ProBudgetScreen from "./screens/pro/ProBudgetScreen";
import ProChatScreen from "./screens/pro/ProChatScreen";
import UpgradeScreen from "./screens/UpgradeScreen";
import AccountScreen from "./screens/AccountScreen";
import { useSubscription } from "./lib/useSubscription";
import NotFoundScreen from "./screens/NotFoundScreen";
import AdminDashboard from "./screens/admin/AdminDashboard";

const PATH_KEY = "moneyschool_path_v1";
const QUIZ_KEY = "moneyschool_quiz_v1";
type AppPath = "starter" | "builder" | "master" | "pro";

const EMPTY_SAVINGS = { allocation: { spending: 70, nestEgg: 30 }, spending: 0, nestEgg: 0, goals: [], transactions: [] };
const EMPTY_DAILY   = { streak: 0, lastCompleted: null, longestStreak: 0, shieldUsedDate: null, earnedBadges: [] };
const EMPTY_PARENT  = { pin: null, milestones: {} };

type DailyEvent = { type: "shielded" | "broken" | "milestone"; streak: number; badgeEmoji?: string; badgeLabel?: string } | null;

function getWeekStart(d: Date): string {
  const copy = new Date(d);
  const day  = copy.getDay();
  copy.setDate(copy.getDate() - day + (day === 0 ? -6 : 1));
  return copy.toISOString().slice(0, 10);
}

export default function App() {
  // Intentionally NOT read from localStorage on mount: the landing page must be
  // the front door on every fresh visit to moneymission.app, even for someone who
  // picked a path before. Returning authenticated Pro subscribers still skip it —
  // see the session-restore effect below, which re-selects "pro" once auth +
  // subscription status resolve.
  const [path, setPath] = useState<AppPath | null>(null);
  const adultMode = path === "pro";
  const [quizDone, setQuizDone] = useState(() => !!localStorage.getItem(QUIZ_KEY));

  function choosePath(p: AppPath) {
    localStorage.setItem(PATH_KEY, p);
    localStorage.setItem(QUIZ_KEY, "1");
    setPath(p);
    setQuizDone(true);
  }

  function completeQuiz(p: AppPath) {
    localStorage.setItem(QUIZ_KEY, "1");
    setQuizDone(true);
    choosePath(p);
  }

  function skipQuizToLanding() {
    localStorage.setItem(QUIZ_KEY, "1");
    setQuizDone(true);
  }

  // Kids state
  const [progress, setProgress] = useState<Record<string, boolean>>(() => loadKidsProgress());
  const [rewardsData, setRewardsData] = useState(() => loadRewards() || EMPTY_REWARDS);
  function persistRewards(next: any) { setRewardsData(next); saveRewards(next); }
  const xp    = rewardsData?.xp    ?? 0;
  const coins = rewardsData?.coins ?? 0;
  const [screen, setScreen] = useState("map");
  const [activeWorld, setActiveWorld] = useState(null);
  const [activeLesson, setActiveLesson] = useState(null);
  const [tab, setTab]     = useState("map");
  const [showConf, setShowConf] = useState(false);

  // Kids — savings, daily challenge, parent portal, missions
  const [savingsData,  setSavingsData]  = useState(() => loadSavings()  || EMPTY_SAVINGS);
  const [dailyData,    setDailyData]    = useState(() => loadDaily()    || EMPTY_DAILY);
  const [parentData,   setParentData]   = useState(() => loadParent()   || EMPTY_PARENT);
  const [missionsData, setMissionsData] = useState(() => loadMissions() || EMPTY_MISSIONS);

  function persistSavings(next: any)  { setSavingsData(next);  saveSavings(next);  }
  function persistDaily(next: any)    { setDailyData(next);    saveDaily(next);    }
  function persistParent(next: any)   { setParentData(next);   saveParent(next);   }
  function persistMissions(next: any) { setMissionsData(next); saveMissions(next); }

  const [lessonHistory, setLessonHistory] = useState<LessonEvent[]>(() => loadLessonHistory());

  const [choresData, setChoresData] = useState(() => loadChores() || EMPTY_CHORES);
  function persistChores(next: any) { setChoresData(next); saveChores(next); }

  function handleChoreSubmit(choreId: string) {
    if ((choresData.pending ?? []).includes(choreId)) return;
    persistChores({ ...choresData, pending: [...(choresData.pending ?? []), choreId] });
  }
  function handleApproveChore(choreId: string, coins: number, choreName: string, choreEmoji: string) {
    const entry = { id: Date.now().toString(), choreId, choreName, choreEmoji, coins, date: new Date().toISOString() };
    persistChores({ ...choresData, pending: (choresData.pending ?? []).filter((x: string) => x !== choreId), history: [...(choresData.history ?? []), entry] });
    persistRewards({ ...rewardsData, coins: rewardsData.coins + coins });
  }
  function handleDenyChore(choreId: string) {
    persistChores({ ...choresData, pending: (choresData.pending ?? []).filter((x: string) => x !== choreId) });
  }

  function handleCompleteMission(id: string, xp: number) {
    persistMissions({ ...missionsData, completed: [...(missionsData.completed ?? []), id] });
    persistRewards({ ...rewardsData, xp: rewardsData.xp + xp });
  }
  function handleSubmitForVerify(id: string) {
    persistMissions({ ...missionsData, pendingVerify: [...(missionsData.pendingVerify ?? []), id] });
  }
  function handleVerifyMission(id: string, xp: number) {
    persistMissions({
      ...missionsData,
      completed:     [...(missionsData.completed ?? []), id],
      pendingVerify: (missionsData.pendingVerify ?? []).filter((x: string) => x !== id),
    });
    persistRewards({ ...rewardsData, xp: rewardsData.xp + xp });
  }
  function handleDenyMission(id: string) {
    persistMissions({ ...missionsData, pendingVerify: (missionsData.pendingVerify ?? []).filter((x: string) => x !== id) });
  }

  function handleDepositAllowance(amount: number) {
    const alloc    = savingsData?.allocation ?? { spending: 70, nestEgg: 30 };
    const rc       = (n: number) => Math.round(n * 100) / 100;
    const spendAmt = rc(amount * alloc.spending / 100);
    const nestAmt  = rc(amount - spendAmt);
    const tx = { id: Date.now().toString(), type: "earn", amount, note: "Allowance 🏠", date: new Date().toISOString() };
    persistSavings({
      ...savingsData,
      spending: rc(savingsData.spending + spendAmt),
      nestEgg:  rc(savingsData.nestEgg  + nestAmt),
      transactions: [tx, ...(savingsData.transactions ?? [])],
    });
    const today = new Date().toISOString().slice(0, 10);
    persistParent({ ...parentData, allowance: { ...(parentData.allowance ?? {}), lastDeposited: today } });
  }

  const streak = dailyData.streak ?? 0;
  const [dailyEvent, setDailyEvent] = useState<DailyEvent>(null);

  // On mount: detect broken streak or activate shield
  useEffect(() => {
    const d = loadDaily();
    if (!d || !d.lastCompleted) return;
    const today     = new Date().toISOString().slice(0, 10);
    const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
    if (d.lastCompleted === today || d.lastCompleted === yesterday) return;

    const daysSince  = Math.floor((Date.now() - new Date(d.lastCompleted).getTime()) / 86400000);
    const prevStreak = d.streak ?? 0;
    if (prevStreak === 0) return;

    const thisWeek       = getWeekStart(new Date());
    const shieldAvailable = d.shieldUsedDate !== thisWeek;

    if (daysSince === 2 && shieldAvailable) {
      // Shield absorbs the one missed day — set lastCompleted to yesterday so streak continues
      persistDaily({ ...d, lastCompleted: yesterday, shieldUsedDate: thisWeek });
      setDailyEvent({ type: "shielded", streak: prevStreak });
    } else {
      // Streak breaks
      persistDaily({ ...d, streak: 0, lastCompleted: null });
      setDailyEvent({ type: "broken", streak: prevStreak });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Adult state
  const [proProgress, setProProgress] = useState<Record<string, boolean>>(() => loadProProgress());
  const [proScreen, setProScreen]     = useState("dashboard");
  const [proWorld, setProWorld]       = useState(null);
  const [proLesson, setProLesson]     = useState(null);
  const [proTab, setProTab]           = useState("dashboard");
  const blank_td = { categories: JSON.parse(JSON.stringify(EMPTY_CATS)), history: [] };
  const [trackerData, setTrackerData] = useState(() => loadTD() || blank_td);
  function persistTracker(next: any) { setTrackerData(_ => next); saveTD(next); }
  const [budgetData, setBudgetData] = useState(() => loadBudget() || EMPTY_BUDGET);
  function persistBudget(next: any) { setBudgetData(next); saveBudget(next); }
  function resetProData() {
    const blank = { categories: JSON.parse(JSON.stringify(EMPTY_CATS)), history: [] };
    persistTracker(blank);
    persistBudget(EMPTY_BUDGET);
    localStorage.removeItem(PLAN_PRIORITY_KEY);
    clearPlanResult();
    clearPlanForm();
  }

  function handleComplete(worldId: any, lessonIdx: any, correct: boolean) {
    const key = `${worldId}-${lessonIdx}`;
    if (!progress[key]) {
      const nextProgress = { ...progress, [key]: true };
      setProgress(nextProgress);
      saveKidsProgress(nextProgress);
      const today = new Date().toISOString().slice(0, 10);
      persistRewards({ ...rewardsData, xp: rewardsData.xp + (correct ? 150 : 100), coins: rewardsData.coins + (correct ? 20 : 10), lastActive: today });
      setShowConf(true);
      setTimeout(() => setShowConf(false), 2500);
      const w = WORLDS.find((w: any) => w.id === worldId);
      const event: LessonEvent = {
        worldId, lessonIdx,
        title: w?.lessons[lessonIdx]?.title ?? "Lesson",
        worldLabel: w?.label ?? "", worldEmoji: w?.emoji ?? "📚",
        completedAt: new Date().toISOString(),
      };
      const nextHistory = [...lessonHistory, event];
      setLessonHistory(nextHistory);
      saveLessonHistory(nextHistory);
      track("lesson_completed", { mode: "kids", worldId, correct });
    }
  }

  function handleProComplete(worldId: any, lessonIdx: any) {
    const key = `${worldId}-${lessonIdx}`;
    const nextProProgress = { ...proProgress, [key]: true };
    setProProgress(nextProProgress);
    saveProProgress(nextProProgress);
    track("lesson_completed", { mode: "pro", worldId });
  }

  function handleChallengeComplete(correct: boolean) {
    const today     = new Date().toISOString().slice(0, 10);
    const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
    const prev      = dailyData.lastCompleted;
    if (prev === today) return;

    const prevStreak   = dailyData.streak ?? 0;
    const newStreak    = prev === yesterday ? prevStreak + 1 : 1;
    const newLongest   = Math.max(dailyData.longestStreak ?? 0, newStreak);
    const earnedBadges: string[] = dailyData.earnedBadges ?? [];
    let newBadges      = [...earnedBadges];
    let milestoneBadge: { emoji: string; label: string } | null = null;

    if (STREAK_MILESTONES.includes(newStreak)) {
      const def = STREAK_BADGES[newStreak];
      if (!earnedBadges.includes(def.id)) {
        newBadges = [...newBadges, def.id];
        milestoneBadge = def;
      }
    }

    persistDaily({ ...dailyData, streak: newStreak, lastCompleted: today, longestStreak: newLongest, earnedBadges: newBadges });
    persistRewards({ ...rewardsData, coins: rewardsData.coins + (correct ? 10 : 5), lastActive: today });

    if (milestoneBadge) {
      setDailyEvent({ type: "milestone", streak: newStreak, badgeEmoji: milestoneBadge.emoji, badgeLabel: milestoneBadge.label });
      setShowConf(true);
      setTimeout(() => setShowConf(false), 3000);
    } else if (correct) {
      setShowConf(true);
      setTimeout(() => setShowConf(false), 2500);
    }
  }

  const worldObj    = WORLDS.find(w => w.id === activeWorld);
  const proWorldObj = PRO_WORLDS.find(w => w.id === proWorld);

  // Resolve equipped cosmetics
  const equippedAvatarEmoji = rewardsData?.equipped?.avatar
    ? (REWARD_ITEMS.find(i => i.id === rewardsData.equipped.avatar)?.emoji ?? "🦸")
    : "🦸";
  const equippedTitleItem = rewardsData?.equipped?.title
    ? REWARD_ITEMS.find(i => i.id === rewardsData.equipped.title)
    : null;
  const heroGradient = THEME_GRADIENTS[rewardsData?.equipped?.theme ?? ""] ?? THEME_GRADIENTS.default;
  function goTab(id: string, sc: string) { setTab(id); setScreen(sc); }
  function goProTab(id: string, sc: string) {
    let resolvedSc = sc;
    if (id === "protracker" && (trackerData?.history?.length ?? 0) > 0) resolvedSc = "protracker";
    if (id === "budget" && Object.keys(budgetData?.months ?? {}).length > 0) resolvedSc = "budget";
    setProTab(id);
    setProScreen(resolvedSc);
  }
  // skipAutoRestore: set to true when the user explicitly clicks "Change Path"
  // so we don't immediately re-route them back to their last session.
  const skipAutoRestore = useRef(false);
  function goHome() { skipAutoRestore.current = true; localStorage.removeItem(PATH_KEY); setPath(null); }

  // Auth + COPPA gating state
  const { session, user, loading: authLoading, signOut, userRole, hasParentalConsent } = useAuth();

  // Admin gate — UX only; real authorization is enforced by server-side JWT checks.
  const isAdmin = !!user?.email && user.email.toLowerCase() === (import.meta.env.VITE_ADMIN_EMAIL ?? "").toLowerCase();
  const [adminOpen, setAdminOpen] = useState(false);

  // Under-13 COPPA: parent-role sessions must not be attributed in analytics.
  useEffect(() => { setAnon(userRole === "parent"); }, [userRole]);
  const { status: subStatus, loading: subLoading, refetch: refetchSub, trial_ends_at, current_period_end, square_subscription_id } = useSubscription(user?.id);
  const paywallOn = import.meta.env.VITE_PAYWALL_ACTIVE === "true";
  const isPro = subStatus === "active" || subStatus === "trialing";

  // Session restore — skip landing page for returning authenticated Pro subscribers.
  // Only fires when: auth done, sub status known, user is signed in as themselves
  // (not a COPPA parent), and path was not intentionally cleared by the user.
  useEffect(() => {
    if (skipAutoRestore.current) return;
    if (authLoading || subLoading || !user || path !== null) return;
    if (userRole === "parent") return;
    if (isPro) choosePath("pro");
  }, [authLoading, subLoading, user, path, isPro, userRole]); // eslint-disable-line react-hooks/exhaustive-deps

  const [isNotFound] = useState(() => {
    const p = window.location.pathname;
    const known = ["/", "/account", "/admin"];
    return !known.includes(p);
  });

  const [specialRoute, setSpecialRoute] = useState<"account" | "admin" | null>(() => {
    const p = window.location.pathname;
    if (p === "/account") return "account";
    if (p === "/admin") return "admin";
    return null;
  });
  const [accountOpen,   setAccountOpen]  = useState(false);
  const [feedbackOpen,  setFeedbackOpen] = useState(false);
  const [landingSignIn, setLandingSignIn] = useState(false);

  const [ageGate, setAgeGate] = useState(() => loadAgeGate());

  function handleAgeAnswer(isAdult: boolean) {
    setAgeGate({ answered: true, isAdult });
  }
  function handleSignOut() {
    signOut();
    clearAgeGate();
    setAgeGate({ answered: false, isAdult: false });
  }

  const MenuProps = {
    adultMode,
    user: user ?? null,
    onSwitchMode: () => adultMode ? goHome() : choosePath("pro"),
    onChangePath: goHome,
    onAccount: () => setAccountOpen(true),
    onSignOut: handleSignOut,
    onFeedback: () => setFeedbackOpen(true),
    isAdmin,
    onAdmin: () => setAdminOpen(true),
  };

  if (isNotFound) return <NotFoundScreen />;

  if (authLoading) {
    return <div style={{ minHeight: "100vh", background: "#042f2e" }} />;
  }

  /* ── ADMIN route ────────────────────────────────────────────────────────────── */
  if ((adminOpen || specialRoute === "admin") && isAdmin && session) {
    return (
      <AdminDashboard
        accessToken={session.access_token}
        onBack={() => {
          setAdminOpen(false);
          if (specialRoute === "admin") {
            setSpecialRoute(null);
            window.history.replaceState({}, "", "/");
          }
        }}
      />
    );
  }

  /* ── LANDING — show first; auth fires after the user picks a mode ── */
  if (path === null) {
    // While checking whether a returning Pro subscriber should be auto-routed,
    // show the loading screen so the landing page doesn't flash before the redirect.
    if (user && subLoading && !skipAutoRestore.current) {
      return <div style={{ minHeight: "100vh", background: "#042f2e" }} />;
    }
    // Returning-user sign-in flow triggered from the landing page
    if (landingSignIn) {
      return (
        <SignInScreen
          mode="self"
          onClose={() => setLandingSignIn(false)}
          onSignedIn={() => setLandingSignIn(false)}
        />
      );
    }
    return (
      <>
        <LandingScreen onChoose={choosePath} onSignIn={() => setLandingSignIn(true)} />
        <InstallBanner />
      </>
    );
  }

  // ── Auth / COPPA gate ─────────────────────────────────────────────────────
  // 1. Age gate fires after mode selection (landing page is pre-auth marketing).
  // 2. Under-13: COPPA-mandatory parent sign-in + consent before any content.
  // 3. Pro mode: sign-in required (Plaid bank sync needs a real user JWT).
  // 4. Kids mode (13+): sign-in is optional — users can explore without an account.
  //
  // Authenticated non-parent users bypass the age gate — they've already verified
  // their email (13+) when they created their account. This prevents the gate from
  // re-appearing on return visits after sign-out clears it.
  const ageVerified  = ageGate.answered || (!!user && userRole !== "parent");
  const isAdultUser  = ageGate.isAdult  || (!!user && userRole !== "parent");

  if (!ageVerified) {
    return <AgeGateScreen onAnswer={handleAgeAnswer} />;
  }
  if (!isAdultUser) {
    if (!user) return <SignInScreen mode="parent" />;
    if (!hasParentalConsent) return (
      <ParentConsentScreen
        parentEmail={user.email || ""}
        onComplete={() => { /* useAuth re-fetches via onAuthStateChange */ }}
      />
    );
  }
  if (path === "pro" && !user) {
    return <SignInScreen mode="self" />;
  }
  // Paywall requires an account to store and look up subscription status
  if (paywallOn && !user) {
    return <SignInScreen mode="self" />;
  }

  /* ── ONBOARDING QUESTIONNAIRE (edge case: path set before quiz completed) ── */
  if (!quizDone) {
    return (
      <>
        <HamburgerMenu {...MenuProps} />
        <OnboardingQuestionnaire onComplete={completeQuiz} onSeeAll={skipQuizToLanding} />
      </>
    );
  }

  /* ── ACCOUNT SCREEN — accessible from any mode ── */
  if (accountOpen || specialRoute === "account") {
    return (
      <div style={{ maxWidth: 480, margin: "0 auto", minHeight: "100vh", position: "relative" }}>
        <AccountScreen
          email={user?.email ?? null}
          status={subStatus}
          trial_ends_at={trial_ends_at}
          current_period_end={current_period_end}
          square_subscription_id={square_subscription_id}
          onSignOut={() => { setAccountOpen(false); handleSignOut(); }}
          onUpgrade={() => { setAccountOpen(false); }}
          onBack={() => {
            setAccountOpen(false);
            setSpecialRoute(null);
            window.history.replaceState({}, "", "/");
          }}
        />
      </div>
    );
  }

  /* ── PAYWALL GATE — single render point, shared across adult and kids mode ── */
  if (paywallOn && !isPro) {
    return (
      <div style={{ maxWidth: 480, margin: "0 auto", minHeight: "100vh" }}>
        <UpgradeScreen onBack={goHome} onSuccess={() => { refetchSub(); goHome(); }} />
      </div>
    );
  }

  /* ── ADULT MODE ── */
  if (adultMode) {
    return (
      <div style={{ maxWidth: 480, margin: "0 auto", background: "#0f172a", minHeight: "100vh", position: "relative", overflow: "hidden" }}>
        <HamburgerMenu {...MenuProps} />
        <BuildInfoBadge />
        <InstallBanner />
        {proScreen === "dashboard" && <ProDashboard progress={proProgress} onSelect={w => { setProWorld(w); setProScreen("world"); }} budgetData={budgetData} onOpenBudget={() => { setProTab("budget"); setProScreen("budget-chat"); }} />}
        {proScreen === "world" && proWorldObj && (
          <ProWorldScreen
            world={proWorldObj}
            progress={proProgress}
            onSelectLesson={i => { setProLesson(i); setProScreen("lesson"); }}
            onBack={() => setProScreen("dashboard")}
          />
        )}
        {proScreen === "lesson" && proWorldObj && proLesson !== null && (
          <ProLessonScreen
            world={proWorldObj}
            lessonIdx={proLesson}
            progress={proProgress}
            onComplete={handleProComplete}
            onBack={() => setProScreen("world")}
          />
        )}
        {(proScreen === "chat" || proScreen === "tracker-chat" || proScreen === "budget-chat") && (
          <ProChatScreen
            key={proScreen}
            trackerData={trackerData}
            budgetData={budgetData}
            proContext={proScreen === "tracker-chat" ? "tracker" : proScreen === "budget-chat" ? "budget" : null}
            onViewFullTracker={() => { setProTab("protracker"); setProScreen("protracker"); }}
            onViewFullBudget={() => { setProTab("budget"); setProScreen("budget"); }}
            onPersistTracker={persistTracker}
            onPersistBudget={persistBudget}
          />
        )}
        {proScreen === "prostats"   && <ProProgressScreen progress={proProgress} />}
        {proScreen === "proplan"    && <ProPlanScreen trackerData={trackerData} persistTracker={persistTracker} onResetPro={resetProData} />}
        {proScreen === "protracker" && <ProTrackerScreen trackerData={trackerData} persistTracker={persistTracker} onResetPro={resetProData} />}
        {proScreen === "budget"     && <ProBudgetScreen budgetData={budgetData} onSave={persistBudget} />}

        {feedbackOpen && (
          <FeedbackModal mode="pro" userEmail={user?.email} onClose={() => setFeedbackOpen(false)} />
        )}

        {proScreen !== "lesson" && (
          <div style={{
            position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)",
            width: "100%", maxWidth: 480, background: "#1e293b",
            borderTop: "2px solid #334155", display: "flex", zIndex: 20,
            boxShadow: "0 -4px 20px #00000055",
          }}>
            {[
              { id: "dashboard",  label: "Courses",  e: "📚", sc: "dashboard"    },
              { id: "proplan",    label: "My Plan",  e: "🎯", sc: "proplan"      },
              { id: "protracker", label: "Tracker",  e: "📈", sc: "tracker-chat" },
              { id: "budget",     label: "Budget",   e: "💰", sc: "budget-chat"  },
              { id: "chat",       label: "MoneyBot", e: "🤖", sc: "chat"         },
              { id: "prostats",   label: "Progress", e: "🏆", sc: "prostats"     },
            ].map(t => (
              <button
                key={t.id}
                onClick={() => goProTab(t.id, t.sc)}
                className="btn-bounce"
                style={{
                  flex: 1, padding: "8px 1px 10px", background: proTab === t.id ? "#334155" : "none",
                  border: "none", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
                  borderTop: proTab === t.id ? "3px solid #0ea5e9" : "3px solid transparent", transition: "background 0.2s",
                }}
              >
                <div style={{ fontSize: 20, filter: proTab === t.id ? "none" : "grayscale(0.5) opacity(0.5)", transition: "filter 0.2s" }}>{t.e}</div>
                <div style={{ fontSize: 9, fontWeight: 900, color: proTab === t.id ? "#0ea5e9" : "#475569", transition: "color 0.2s" }}>{t.label}</div>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  /* ── KIDS MODE ── */
  return (
    <>
      {showConf && <Confetti />}
      <div style={{ maxWidth: 480, margin: "0 auto", background: path === "master" ? "#0f172a" : "#fff7ed", minHeight: "100vh", position: "relative", overflow: "hidden" }}>
        <HamburgerMenu {...MenuProps} />
        <BuildInfoBadge />
        <InstallBanner />
        {screen === "map" && (
          <WorldMap
            progress={progress}
            tier={path as "starter" | "builder" | "master"}
            onSelect={w => { setActiveWorld(w); setScreen("world"); }}
            dailyData={dailyData}
            onChallengeComplete={handleChallengeComplete}
            dailyEvent={dailyEvent}
            onDismissDailyEvent={() => setDailyEvent(null)}
            heroGradient={heroGradient}
            equippedTitle={equippedTitleItem}
          />
        )}
        {screen === "world" && worldObj && (
          <WorldScreen
            world={worldObj}
            progress={progress}
            onSelectLesson={i => { setActiveLesson(i); setScreen("lesson"); }}
            onBack={() => setScreen("map")}
            tier={path}
          />
        )}
        {screen === "lesson" && worldObj && activeLesson !== null && (
          <LessonScreen
            world={worldObj}
            lessonIdx={activeLesson}
            progress={progress}
            onComplete={handleComplete}
            onBack={() => setScreen("world")}
            tier={path}
          />
        )}
        {screen === "leaderboard" && (
          <LeaderboardScreen
            xp={xp}
            streak={streak}
            lessonsCompleted={Object.values(progress).filter(Boolean).length}
            tier={path as string}
            onBack={() => setScreen("stats")}
          />
        )}
        {screen === "chat"    && <ChatScreen path={path as any} />}
        {screen === "games"   && <GamesScreen tier={path as "starter" | "builder" | "master"} />}
        {screen === "stats"   && (
          <ProgressScreen
            progress={progress} xp={xp} coins={coins} streak={streak}
            tier={path}
            parentData={parentData}
            onParentPortal={() => setScreen("parent")}
            onLeaderboard={() => setScreen("leaderboard")}
            rewardsData={rewardsData}
            onSaveRewards={persistRewards}
            equippedAvatarEmoji={equippedAvatarEmoji}
            missionsData={missionsData}
            onCompleteMission={handleCompleteMission}
            onSubmitForVerify={handleSubmitForVerify}
          />
        )}
        {screen === "savings" && (
          <KidsSavingsScreen data={savingsData} onSave={persistSavings} choresData={choresData} onChoreSubmit={handleChoreSubmit} />
        )}
        {screen === "whatif"     && <WhatIfScreen />}
        {screen === "scenarios"  && <ScenariosScreen tier={path as string} />}
        {screen === "parent"  && (
          <ParentPortalScreen
            parentData={parentData} onSave={persistParent}
            progress={progress} xp={xp} coins={coins} streak={streak}
            savingsData={savingsData}
            missionsData={missionsData}
            onVerifyMission={handleVerifyMission}
            onDenyMission={handleDenyMission}
            onBack={() => setScreen("stats")}
            choresData={choresData}
            onApproveChore={handleApproveChore}
            onDenyChore={handleDenyChore}
            onSaveChores={persistChores}
            tier={path as string}
            lessonHistory={lessonHistory}
            earnedBadges={dailyData.earnedBadges ?? []}
            onDepositAllowance={handleDepositAllowance}
            rewardsData={rewardsData}
          />
        )}

        {feedbackOpen && (
          <FeedbackModal mode={path} userEmail={user?.email} onClose={() => setFeedbackOpen(false)} />
        )}

        {screen !== "lesson" && (
          <div style={{
            position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)",
            width: "100%", maxWidth: 480,
            background: path === "master" ? "#1e293b" : "#fff",
            borderTop: path === "master" ? "2px solid #334155" : "3px solid #fef3c7",
            display: "flex", zIndex: 20,
            boxShadow: path === "master" ? "0 -4px 20px #00000055" : "0 -4px 20px #00000011",
          }}>
            {[
              { id: "map",       label: "Map",      e: "🗺️", sc: "map"       },
              { id: "scenarios", label: "Scenes",   e: "🎭", sc: "scenarios" },
              { id: "games",     label: "Practice", e: "🎯", sc: "games"     },
              { id: "chat",      label: "MoneyBot", e: "🤖", sc: "chat"      },
              { id: "savings",   label: "Money",    e: "💰", sc: "savings"   },
              { id: "whatif",    label: "What If",  e: "📈", sc: "whatif"    },
              { id: "stats",     label: "Progress", e: "🏆", sc: "stats"     },
            ].map(t => (
              <button
                key={t.id}
                onClick={() => goTab(t.id, t.sc)}
                className="btn-bounce"
                style={{
                  flex: 1, padding: "8px 1px 10px",
                  background: tab === t.id ? (path === "master" ? "#334155" : "#fff7ed") : "none",
                  border: "none", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
                  borderTop: tab === t.id
                    ? `3px solid ${path === "master" ? "#6366f1" : "#f97316"}`
                    : "3px solid transparent",
                  transition: "background 0.2s",
                }}
              >
                <div style={{
                  fontSize: 20,
                  filter: tab === t.id ? "none" : "grayscale(0.4) opacity(0.55)",
                  animation: tab === t.id && path !== "master" ? "bounce 2s infinite" : "none",
                  transition: "filter 0.2s",
                }}>{t.e}</div>
                <div style={{
                  fontSize: 8, fontWeight: 900, transition: "color 0.2s",
                  color: tab === t.id
                    ? (path === "master" ? "#818cf8" : "#f97316")
                    : (path === "master" ? "#475569" : "#9ca3af"),
                }}>{t.label}</div>
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
